import { DownloadSimple } from "@phosphor-icons/react";
import Button from "../Button";
import DateRangeFilterDropdown, {
    useDateRangeFilterDropdown,
} from "../DateRangeFilterDropdown";
import FormDropdown from "../FormDropdown";
import FormInputLabel from "../FormInputLabel";
import { Controller, useForm } from "react-hook-form";
import {
    DownloadActivityDataReportFormValues,
    DownloadActivityDataReportFormValuesWithFormattedDates,
    defaultDownloadActivityDataReportFormValues,
    downloadActivityDataReportFormSchema,
} from "./DownloadActivityReportFormValues";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import { reportTypeOptions } from "../../constants";

type DownloadActivityReportFormProps = {
    onSubmit: (
        values: DownloadActivityDataReportFormValuesWithFormattedDates
    ) => void;
    isSubmitting: boolean;
};

const DownloadActivityReportForm = ({
    onSubmit,
    isSubmitting,
}: DownloadActivityReportFormProps) => {
    const {
        value: dateRangeValue,
        label,
        handleChange,
        startDate,
        endDate,
    } = useDateRangeFilterDropdown();

    const methods = useForm<DownloadActivityDataReportFormValues>({
        mode: "onSubmit",
        resolver: yupResolver(downloadActivityDataReportFormSchema),
        defaultValues: defaultDownloadActivityDataReportFormValues,
    });

    const handleFormSubmit = async (
        values: DownloadActivityDataReportFormValues
    ) => {
        onSubmit({ ...values, fromDate: startDate, toDate: endDate });
    };

    useEffect(() => {
        methods.setValue("dateRange", dateRangeValue);
    }, [dateRangeValue, methods]);

    return (
        <form
            className="flex flex-col gap-6 md:flex-row"
            onSubmit={methods.handleSubmit(handleFormSubmit)}
        >
            <div className="flex flex-col gap-6 md:flex-1 md:grid md:grid-cols-2">
                <div>
                    <FormInputLabel>Report Type</FormInputLabel>
                    <Controller
                        name="type"
                        control={methods.control}
                        render={({ field: { onChange, value } }) => (
                            <FormDropdown
                                pleaseSelectText="Select report type"
                                value={value}
                                options={reportTypeOptions}
                                onChange={onChange}
                                error={methods.formState.errors.type}
                                className="w-auto"
                                position="top"
                            />
                        )}
                    />
                </div>
                <div>
                    <FormInputLabel>Date range</FormInputLabel>
                    <Controller
                        name="dateRange"
                        control={methods.control}
                        render={({ field: { onChange, value } }) => (
                            <DateRangeFilterDropdown
                                value={value}
                                onChange={handleChange}
                                selectedTextOverride={label}
                                disabledOptions={["all"]}
                                className="!max-w-none"
                                inputClassName="!rounded !w-full"
                                position="top"
                            />
                        )}
                    />
                </div>
            </div>
            <div className="flex-none md:pt-6">
                <Button
                    className="gap-2 h-[3.25rem]"
                    type="submit"
                    loading={isSubmitting}
                >
                    Download <DownloadSimple size={20} />
                </Button>
            </div>
        </form>
    );
};

export default DownloadActivityReportForm;
